import apiClient from '../utils/apiClient';
import { EligibilityData } from '../types';

export interface EligibilityFilter {
    activeStatus?: string;
    groupId?: string;
    planCode?: string;
    // MemberId is called "FamilyId" in Four Winds data and "IdNumber" in MedOne data
    memberId?: string;
    effectiveDateStart?: string;
    effectiveDateEnd?: string;
    includeDeleted?: boolean;
    favoritesOnly?: boolean;
    favoritesOnTop?: boolean;
    sortField?: string;
    sortDirection?: string;
    dataProviderId?: number;
    lastName?: string;
    firstName?: string;
    ssn?: string;
    dateOfBirth?: string;
    page?: number;
    pageSize?: number;
}

export interface PagedEligibilityResponse {
    data: EligibilityDataResponse[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// API response type with string dates (before conversion to Dayjs)
export interface EligibilityDataResponse {
    id: number;
    primarySubscriber: boolean;
    relationshipCode: string;
    activeStatus?: string;
    employmentStatus?: string;
    // MemberId is called "FamilyId" in Four Winds data and "IdNumber" in MedOne data
    memberId: string;
    planCode?: string;
    groupId: string;
    hireDate?: string;
    lastName: string;
    firstName: string;
    middleName?: string;
    suffix?: string;
    ssn: string;
    email?: string;
    homePhone?: string;
    workPhone?: string;
    streetAddress1: string;
    streetAddress2?: string;
    city: string;
    state: string;
    zip: string;
    dateOfBirth: string;
    gender: string;
    coverageType?: string;
    coverageTier?: string;
    effectiveDate: string;
    endDate?: string;
    // MedOne specific fields
    alternateId?: string;
    previousId?: string;
    personCode?: string;
    groupStartDate?: string;
    groupEndDate?: string;
    subGroupStartDate?: string;
    subGroupEndDate?: string;
    subGroupCode?: string;
    location?: string;
    futureUse?: string;
    chronicCondition?: string;
    diagnosisCode1?: string;
    diagnosisCode2?: string;
    diagnosisCode3?: string;
    diagnosisCode4?: string;
    benefitOverrideStartDay?: string;
    benefitOverrideStartMonth?: string;
    isStudent?: boolean;
    preferredLanguage?: string;
    mobilePhone?: string;
    rejectPrimaryClaim?: string;
    clientCustom1?: string;
    clientCustom2?: string;
    clientCustom3?: string;
    clientCustom4?: string;
    clientCustom5?: string;
    // Audit fields
    createdAt: string;
    updatedAt?: string;
    isDeleted: boolean;
    isFavorite: boolean;
    dataProviderId?: number;
    dataProviderName?: string;
}

export const getEligibilityData = async (filter?: EligibilityFilter): Promise<PagedEligibilityResponse> => {
    let url = 'api/EligibilityData';
    
    const params = new URLSearchParams();
    
    // Add pagination parameters with defaults
    params.append('page', (filter?.page ?? 0).toString());
    params.append('pageSize', (filter?.pageSize ?? 25).toString());
    
    // Add filter parameters
    if (filter?.activeStatus) params.append('activeStatus', filter.activeStatus);
    if (filter?.groupId) params.append('groupId', filter.groupId);
    if (filter?.planCode) params.append('planCode', filter.planCode);
    if (filter?.memberId) params.append('memberId', filter.memberId);
    if (filter?.firstName) params.append('firstName', filter.firstName);
    if (filter?.lastName) params.append('lastName', filter.lastName);
    if (filter?.ssn) params.append('ssn', filter.ssn);
    if (filter?.dateOfBirth) params.append('dateOfBirth', filter.dateOfBirth);
    if (filter?.effectiveDateStart) params.append('effectiveDateStart', filter.effectiveDateStart);
    if (filter?.effectiveDateEnd) params.append('effectiveDateEnd', filter.effectiveDateEnd);
    if (filter?.includeDeleted !== undefined) params.append('isDeleted', filter.includeDeleted.toString());
    if (filter?.favoritesOnly !== undefined) params.append('favoritesOnly', filter.favoritesOnly.toString());
    if (filter?.favoritesOnTop !== undefined) params.append('favoritesOnTop', filter.favoritesOnTop.toString());
    if (filter?.sortField) params.append('sortField', filter.sortField);
    if (filter?.sortDirection) params.append('sortDirection', filter.sortDirection);
    if (filter?.dataProviderId) params.append('dataProviderId', filter.dataProviderId.toString());
    
    url += `?${params.toString()}`;

    return apiClient.get<PagedEligibilityResponse>(url);
};

export const getEligibilityDataById = async (id: number): Promise<EligibilityDataResponse> => {
    return apiClient.get<EligibilityDataResponse>(`api/EligibilityData/${id}`);
};

export const createEligibilityData = async (eligibilityData: EligibilityData): Promise<EligibilityDataResponse> => {
    return apiClient.post<EligibilityDataResponse>('api/EligibilityData', eligibilityData);
};

export const updateEligibilityData = async (id: number, eligibilityData: EligibilityData): Promise<EligibilityDataResponse> => {
    return apiClient.patch<EligibilityDataResponse>(`api/EligibilityData/${id}`, eligibilityData);
};

export const deleteEligibilityData = async (id: number): Promise<void> => {
    await apiClient.delete(`api/EligibilityData/${id}`);
};

export const toggleFavoriteStatus = async (id: number): Promise<void> => {
    await apiClient.patch(`api/EligibilityData/${id}/favorite`, {});
};

export const getRecentlyUpdatedEligibilityData = async (days: number = 7, limit?: number): Promise<EligibilityDataResponse[]> => {
    // Calculate date range for recent updates
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    // Use the existing paginated endpoint with proper filtering
    const filter: EligibilityFilter = {
        sortField: 'updatedAt',
        sortDirection: 'desc',
        page: 0,
        pageSize: limit || 50, // Use limit or default to 50 for recent updates
        includeDeleted: false
    };
    
    try {
        const response = await getEligibilityData(filter);
        
        // Filter for records that were actually updated (not just created) in the specified time range
        // or created within the time range (for new records)
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        const recentUpdates = response.data.filter(item => {
            // Include if updated within the time range
            if (item.updatedAt) {
                const updatedDate = new Date(item.updatedAt);
                if (updatedDate >= cutoffDate) {
                    return true;
                }
            }
            
            // Include if created within the time range (new records)
            const createdDate = new Date(item.createdAt);
            return createdDate >= cutoffDate;
        });
        
        return recentUpdates;
    } catch (error) {
        console.error('Failed to fetch recent eligibility updates:', error);
        return [];
    }
};
